import { useCallback, useEffect, useState } from 'react'
import {
  activateWaitingServiceWorker,
  checkForServiceWorkerUpdate
} from '../pwa/register-service-worker'
import {
  getRegistrationFromPWAEvent,
  PWA_OFFLINE_READY,
  PWA_UPDATE_AVAILABLE
} from '../pwa/pwa-events'

/**
 * Gestisce eventi PWA: update disponibile, check su visibility, attivazione SW.
 * @returns {{
 *   showUpdateAvailable: boolean,
 *   showOfflineReady: boolean,
 *   applyUpdate: () => void,
 *   dismissUpdate: () => void,
 *   dismissOfflineReady: () => void
 * }}
 */
export function usePWAEvents() {
  const [updateRegistration, setUpdateRegistration] = useState(
    /** @type {ServiceWorkerRegistration | null} */ (null)
  )
  const [showOfflineReady, setShowOfflineReady] = useState(false)

  useEffect(() => {
    const onUpdateAvailable = (event) => {
      setUpdateRegistration(getRegistrationFromPWAEvent(event))
    }

    const onOfflineReady = () => {
      setShowOfflineReady(true)
    }

    window.addEventListener(PWA_UPDATE_AVAILABLE, onUpdateAvailable)
    window.addEventListener(PWA_OFFLINE_READY, onOfflineReady)

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForServiceWorkerUpdate().catch(() => {})
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    checkForServiceWorkerUpdate().catch(() => {})

    return () => {
      window.removeEventListener(PWA_UPDATE_AVAILABLE, onUpdateAvailable)
      window.removeEventListener(PWA_OFFLINE_READY, onOfflineReady)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  const applyUpdate = useCallback(() => {
    activateWaitingServiceWorker(updateRegistration)
  }, [updateRegistration])

  const dismissUpdate = useCallback(() => {
    setUpdateRegistration(null)
  }, [])

  const dismissOfflineReady = useCallback(() => {
    setShowOfflineReady(false)
  }, [])

  return {
    showUpdateAvailable: updateRegistration != null,
    showOfflineReady,
    applyUpdate,
    dismissUpdate,
    dismissOfflineReady
  }
}

export default usePWAEvents
